import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Check } from "lucide-react";
import { useModel } from "@/contexts/ModelContext";
import { LogoWithFallback } from "@/components/LogoWithFallback";

/**
 * HeaderPoweredByPicker — compact "Powered by …" dropdown for the top header.
 *
 * Replaces the old in-hero "Powered by" chip strip. Sits to the right of the
 * Wikificial wordmark with comfortable spacing, uses a small label so it
 * doesn't crowd the nav, and opens a menu of frontier models with logo +
 * vendor name. Selection routes through the same global ModelContext.
 */
export function HeaderPoweredByPicker() {
  const { models, model, setModelId } = useModel();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey  = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [open]);

  return (
    <div ref={ref} className="relative hidden md:block" data-testid="header-powered-by">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={`Model: ${model.name}`}
        className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-white px-2 py-1 rounded hover:bg-white/10 transition-colors"
        data-testid="header-powered-by-trigger"
      >
        <LogoWithFallback
          urls={model.logos}
          letter={model.name.slice(0, 1)}
          color={model.dot}
          size={14}
          bare
          alt={model.name}
        />
        <span className="leading-none">{model.name}</span>
        <ChevronDown size={10} className={`text-white/70 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute left-0 top-full mt-1 w-[220px] rounded-lg bg-white border border-[#E7EAF0] shadow-lg z-50 py-1"
          data-testid="header-powered-by-menu"
        >
          <div className="px-3 pt-2 pb-1 text-[9.5px] uppercase tracking-widest font-bold text-[#8A93A6]">
            Frontier Model
          </div>
          {models.map(m => {
            const active = m.id === model.id;
            return (
              <button
                key={m.id}
                role="option"
                aria-selected={active}
                onClick={() => { setModelId(m.id); setOpen(false); }}
                data-testid={`header-powered-by-option-${m.id}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-[12.5px] transition-colors ${
                  active ? "bg-[#F7F8FA]" : "hover:bg-[#F6F7FB]"
                }`}
              >
                <LogoWithFallback
                  urls={m.logos}
                  letter={m.name.slice(0, 1)}
                  color={m.dot}
                  size={18}
                  bare
                  alt={m.name}
                />
                <span className="flex-1 text-left">
                  <span className="font-semibold text-[#0B1220] block leading-none">{m.name}</span>
                  <span className="text-[10.5px] text-[#5B6478]">{m.vendor}</span>
                </span>
                {active && <Check size={12} className="text-[#0B1220]"/>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
